import { Location } from "@angular/common";
import { AfterViewInit, Component, ElementRef, ViewChild } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

import { BaseComponent, ContentService, environment, EventBusService, HelperService, Lesson, ProductService, StorageService, StorageKeys, UserService, Video } from "@mypxplat/xplat/core";
import { AppService, BetaService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { filter, takeUntil } from "rxjs/operators";
import { FuiContentFilterComponent } from "../fender-ui";
import { BreadcrumbItem } from "../fender-ui/layout-header/layout-header.component";
import { ClipboardService } from "ngx-clipboard";
import { FilterableVideoGroupComponent } from "../filterable-video-group/filterable-video-group.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "fui-learn",
  templateUrl: "fui-learn.component.html",
  standalone: false,
})
export class FuiLearnComponent extends BaseComponent implements AfterViewInit {
  public breadcrumbs: BreadcrumbItem[] = [];
  public env = environment;
  public displayedSection: string;
  public displayedFolder: any;
  public displayedVideoGroup: {
    videos: Array<Video>;
    title: string;
    description?: string;
    ignoreBrand?: boolean;
  };
  public displayedTag: any;
  public displayedProduct: any;
  public refreshing: boolean = false;
  public myList: Array<any> = [];
  public savedVideos: Array<Video> = [];
  public inProgressVideos: Array<Video> = [];
  public educationData: any;
  public featuredLessons: Array<Lesson>;
  public featuredVideos: Array<Video>;
  public myProductsWithVideos: Array<any>;
  public routerEvents: any;
  public selectedCourseIndex: number = 0;
  public filters: any = {
    sort_by: "date",
    sort_order: "desc",
    product: "any",
    tag: "any",
    duration: "any",
    level: "any",
    query: "",
  };

  public filteredVideos: Array<Video>;
  public displayedVideos: Array<Video>;
  public displayedFolders: Array<any>;
  public displayedLessons: Array<any>;
  public limit: number = 20;
  public page: number = 0;
  public totalPages: number;
  public totalVideos: number;
  public lessonLoadingMap: any = {};
  public lessonCopiedMap: any = {};

  @ViewChild("fuiContentFilter") fuiContentFilter: FuiContentFilterComponent;
  @ViewChild("fuiSidebarFilter") fuiSidebarFilter: ElementRef;
  @ViewChild("sidebarFilterModal") sidebarFilterModal: any;
  @ViewChild("filterableVideoGroup") filterableVideoGroup: FilterableVideoGroupComponent;
  @ViewChild("featuredVideoIframe") featuredVideoIframe?: ElementRef<HTMLIFrameElement>;
  @ViewChild("featuredVideoContainer") featuredVideoContainer?: ElementRef<HTMLElement>;

  public featuredFenderVideoId: number = environment.featuredFenderVideoId;
  public featuredFenderStudioVideoId: number = environment.featuredFenderStudioVideoId;
  public featuredVideoId: number = this.appService.videoContentType.presonus ? this.featuredFenderStudioVideoId : this.featuredFenderVideoId;
  public featuredVideo: Video;
  public featuredVideoEmbedUrl: SafeResourceUrl | null = null;
  private _featuredVideoPaused: boolean = false;
  private _featuredVideoPausedManually: boolean = false;
  private _featuredVideoPausedByScroll: boolean = false;
  private _featuredVideoPausedByExternal: boolean = false;
  private _featuredVideoInView: boolean = true;
  private _featuredVideoIntersectionObserver?: IntersectionObserver;
  public videosInitted: boolean = false;
  constructor(
    userService: UserService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _storage: StorageService,
    public appService: AppService,
    public betaService: BetaService,
    public communityService: WebCommunityService,
    public contentService: ContentService,
    public eventBusService: EventBusService,
    public helperService: HelperService,
    public location: Location,
    public productService: ProductService,
    public translate: TranslateService,
    private _clipboardService: ClipboardService,
    private _sanitizer: DomSanitizer
  ) {
    super(userService);
  }

  ngOnInit() {
    this.breadcrumbs = [{ label: "header.nav.home", routerLink: ["/home"] }, { label: "fui-header.learn" }];
    this.init();

    // Pause the hero video whenever any video playback starts elsewhere.
    this.eventBusService
      .observe(this.eventBusService.types.playVideo)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pauseFeaturedVideo("external"));

    // Resume the hero video when the global player closes.
    this.eventBusService
      .observe(this.eventBusService.types.videoClosed)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resumeFeaturedVideo("external"));

    this.routerEvents = this._router.events.subscribe((result) => {
      // this allows you to "navigate" to another video list. Used for when users click a tag in a video grid.
      if (result instanceof NavigationEnd) {
        if (result.url.indexOf("learn/videos") > -1) this.init(true);
      }
    });
  }

  ngAfterViewInit() {
    // Auto-pause the hero when it scrolls out of view.
    if (typeof IntersectionObserver === "undefined") return;
    const el = this.featuredVideoContainer?.nativeElement;
    if (!el) return;

    this._featuredVideoIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const inView = entry.isIntersecting && entry.intersectionRatio >= 0.35;
        this._featuredVideoInView = inView;
        if (!inView) {
          this.pauseFeaturedVideo("scroll");
        } else {
          this.resumeFeaturedVideo("scroll");
        }
      },
      { threshold: [0, 0.15, 0.35, 0.5, 0.75, 1] }
    );

    this._featuredVideoIntersectionObserver.observe(el);
  }

  selectFolder(folder) {
    this.appService.storedBodyPosition = document.body.scrollTop || document.documentElement.scrollTop;
    this.displayedFolder = folder;
    this.displayedVideoGroup = {
      videos: [...folder.videos],
      title: folder.title,
      description: folder.description,
      ignoreBrand: true,
    };
    this.location.replaceState("learn/videos?folder=" + this.displayedFolder.string_id);
    this.appService.scrollBodyTo(0);
  }

  showAllVideos(focusInput: boolean = false) {
    this.appService.storedBodyPosition = document.body.scrollTop || document.documentElement.scrollTop;
    this.displayedSection = undefined;
    this.displayedVideoGroup = {
      videos: [...this.educationData.videos],
      title: "All Videos",
      description: "Search our entire library of videos.",
    };
    this.location.replaceState("learn/videos/search");
    this.appService.scrollBodyTo(0);
    if (focusInput) {
      setTimeout(() => {
        const input = document.getElementById("content-filter-search");
        if (input) input.focus();
      }, 100);
    }
  }

  goBack() {
    if (this.displayedSection == "mylist") {
      this.displayedVideoGroup = undefined;
      setTimeout(() => {
        this.appService.scrollBodyTo(this.appService.storedBodyPosition);
      }, 100);
    } else {
      this.displayedFolder = undefined;
      this.displayedVideoGroup = undefined;
      this.displayedSection = "videos";
      this.filterableVideoGroup.clearFilter();
      let routeTo = "learn/videos";
      this.location.replaceState(routeTo);
      setTimeout(() => {
        this.appService.scrollBodyTo(this.appService.storedBodyPosition);
      }, 100);
    }
  }

  toggleVideoContentType(brand) {
    let existing = this.appService.videoContentType;
    existing[brand] = !existing[brand];
    this.appService.videoContentType = existing;
    if (this.displayedSection == "courses" && !this.appService.videoContentType.presonus) {
      this.switchSection("videos");
    }
    if (this.filterableVideoGroup) this.filterableVideoGroup.videoContentTypeChanged();
    this.videoContentTypeChanged();
  }

  public playFolder: any;

  init(force?) {
    this.displayedSection = this._activatedRoute.snapshot.params["category"];

    this.contentService.educationDataStream$
      .pipe(
        takeUntil(this.destroy$),
        filter((item) => !!item)
      )
      .subscribe((result) => {
        if (this.videosInitted && !force) return;
        this.videosInitted = true;
        this.educationData = result;
        this.educationData.videos.sort((a, b) => (a.created > b.created ? -1 : 1));
        this.featuredLessons = [...this.contentService.lessons.filter((item, index) => item.status != "deleted" && item.status != "pending" && index < 5)];
        this.featuredLessons.sort((a, b) => a.order - b.order);
        this.educationData.lessons = [...this.educationData.lessons.filter((item) => item.status != "deleted" && item.status != "pending")];
        this.educationData.lessons.sort((a, b) => a.order - b.order);
        this.educationData.courses.sort((a, b) => a.order - b.order);

        let productStringId = this._activatedRoute.snapshot.queryParams["product"];
        let folderStringId = this._activatedRoute.snapshot.queryParams["folder"];
        let tagStringId = this._activatedRoute.snapshot.queryParams["tag"];
        if (productStringId || folderStringId || tagStringId) {
          if (folderStringId) {
            this.chooseFolder(this.educationData.folders.filter((item) => item.string_id == folderStringId)[0]);
          } else if (tagStringId) {
            this.chooseTag(this.contentService.sidebarTags.filter((item) => item.string_id == tagStringId)[0]);
          }
        }

        this.productService.products$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
          if ((this.appService.videoContentType.presonus && this.appService.videoContentType.fender) || (!this.appService.videoContentType.presonus && !this.appService.videoContentType.fender)) {
            // all
            this.featuredVideos = this.educationData.videos.filter((item, index) => index < 8);
          } else {
            // fender or presonus
            const selectedBrand = this.appService.videoContentType.fender ? "fender" : "presonus";
            this.featuredVideos = this.educationData.videos.filter((item) => item.brand == selectedBrand || item.brand == "both").filter((item, index) => index < 8);
          }
          this.playFolder = this.educationData.folders.filter((item) => item.title == "Introduction to Fender Play")[0];
          this.myProductsWithVideos = [];
          this.contentService.productsAssociatedWithVideos.forEach((item) => {
            if (this.productService.productsByStringIDs[item.stringId]) this.myProductsWithVideos.push(this.productService.productsByStringIDs[item.stringId]);
          });
          if (productStringId) {
            this.chooseProduct(this.myProductsWithVideos.filter((item) => item.stringId == productStringId)[0]);
          }
          this.initFeaturedVideo();
          this.videoContentTypeChanged();
          if (this.filterableVideoGroup) this.filterableVideoGroup.videoContentTypeChanged();
        });
      });
    if (!this.educationData) this.refresh();
  }

  initFeaturedVideo() {
    this.featuredVideoId = this.appService.videoContentType.presonus ? this.featuredFenderStudioVideoId : this.featuredFenderVideoId;
    if (this.featuredVideoId) {
      this.featuredVideo = this.educationData.videos.filter((item) => item.id == this.featuredVideoId)[0];
      this.featuredVideoEmbedUrl = this.featuredVideo ? this.buildYoutubeEmbedUrl(this.featuredVideo.platform_id) : null;
    }
  }

  refresh() {
    this.refreshing = true;
    this.contentService.getEducationData(false, true).subscribe((result) => (this.refreshing = false));
  }

  public showFilterOptions: boolean = true;
  closeFilterOptions() {
    this.showFilterOptions = false;
  }

  clearFilter(filter?) {
    if (filter == "product") {
      this.filters.product = "any";
    } else if (filter == "tag") {
      this.filters.tag = "any";
    } else if (filter == "duration") {
      this.filters.duration = "any";
    } else if (filter == "level") {
      this.filters.level = "any";
    } else if (!filter) {
      this.filters.query = "";
      this.filters.product = "any";
      this.filters.tag = "any";
      this.filters.duration = "any";
      this.filters.level = "any";
    }
  }

  showMoreVideos(videos, title?: string, description?: string, translated?: boolean) {
    this.appService.storedBodyPosition = document.body.scrollTop || document.documentElement.scrollTop;
    this.displayedVideoGroup = {
      videos: [...videos],
      title: translated && title ? this.translate.instant(title) : title,
      description: translated && description ? this.translate.instant(description) : description,
    };
    this.appService.scrollBodyTo(0);
  }

  filter() {
    this.displayedFolders = this.educationData?.folders ? [...this.educationData.folders].sort((a, b) => a.order - b.order) : [];
    this.displayedLessons = this.educationData?.lessons ? [...this.educationData.lessons].sort((a, b) => a.order - b.order) : [];
    if ((this.appService.videoContentType.fender && this.appService.videoContentType.presonus) || (!this.appService.videoContentType.fender && !this.appService.videoContentType.presonus)) {
      this.displayedFolders = [...this.educationData.folders].sort((a, b) => a.order - b.order);
      this.displayedLessons = [...this.educationData.lessons].sort((a, b) => a.order - b.order);
    } else {
      const selectedBrand = this.appService.videoContentType.fender ? "fender" : "presonus";
      this.displayedFolders = [...this.educationData.folders.filter((item) => item.brand == selectedBrand || item.brand == "both")].sort((a, b) => a.order - b.order);
      this.displayedLessons = [...this.educationData.lessons.filter((item) => item.brand == selectedBrand || item.brand == "both")].sort((a, b) => a.order - b.order);
    }
  }

  videoContentTypeChanged() {
    this.filter();
  }

  switchSection(section) {
    this.displayedVideoGroup = undefined;
    if (section != this.displayedSection) {
      this.displayedSection = section;
      let routeTo = "learn/" + section + "/";
      this.location.replaceState(routeTo);
      // this._router.navigate(routeTo, { replaceUrl: true });
    }
  }

  viewLesson(lesson) {
    this._router.navigate(["/learn/lesson", lesson.id]);
  }

  chooseFolder(folder) {
    this.displayedTag = undefined;
    this.filters.tag = "any";
    if (this.displayedSection != "videos") this.switchSection("videos");
    this.displayedFolder = folder;
    this.displayedVideos = this.displayedFolder.videos;
    this.location.replaceState("learn/videos?folder=" + this.displayedFolder.string_id);
    this.displayedVideoGroup = {
      videos: [...this.displayedFolder.videos],
      title: this.displayedFolder.title,
      description: this.displayedFolder.description,
      ignoreBrand: true,
    };
  }

  chooseTag(tag) {
    this.showAllVideos();
    setTimeout(() => {
      if (this.filterableVideoGroup) this.filterableVideoGroup.chooseTag(tag);
    }, 100);
    this.location.replaceState("learn/videos?tag=" + tag.string_id);
  }

  chooseProduct(product) {
    this.showAllVideos();
    setTimeout(() => {
      if (this.filterableVideoGroup) this.filterableVideoGroup.chooseProduct(product);
    }, 100);
  }

  toggleSaveLesson(lesson) {
    this.lessonLoadingMap[lesson.id] = true;
    let existingData = this.contentService.userLearnData[lesson.type == "lesson" ? "lessons" : "courses"][lesson.id];
    let saved = existingData?.enrolled ? false : true;
    let args: any = {
      user_id: this.user.id,
      learn_group_id: lesson.id,
      enrolled: saved,
    };
    if (!existingData?.date_started) args.date_started = new Date();

    this.contentService.saveUserLearnGroupData(lesson.id, args).subscribe({
      next: (result) => {
        this.lessonLoadingMap[lesson.id] = false;
      },
      error: (error) => {
        this.appService.alertError(error);
        this.lessonLoadingMap[lesson.id] = false;
      },
    });
  }

  copyLink(lesson) {
    this.lessonCopiedMap[lesson.id] = true;
    this._clipboardService.copy(environment.mypresonusUrl + "learn/" + lesson.type + "/" + lesson.id);
    event.stopPropagation();
    setTimeout(() => {
      this.lessonCopiedMap[lesson.id] = false;
    }, 1000);
  }

  ngOnDestroy() {
    if (this.routerEvents && this.routerEvents.unsubscribe) this.routerEvents.unsubscribe();
    if (this._featuredVideoIntersectionObserver) this._featuredVideoIntersectionObserver.disconnect();
    super.ngOnDestroy();
  }

  pauseFeaturedVideo(reason: "manual" | "scroll" | "external" = "manual") {
    if (reason === "manual") this._featuredVideoPausedManually = true;
    if (reason === "scroll") this._featuredVideoPausedByScroll = true;
    if (reason === "external") this._featuredVideoPausedByExternal = true;

    this._featuredVideoPaused = true;
    this.postToFeaturedYoutubePlayer("pauseVideo");
  }

  resumeFeaturedVideo(reason: "scroll" | "external" = "external") {
    // Only auto-resume when it's safe to do so.
    if (this._featuredVideoPausedManually) return;
    if (!this._featuredVideoInView) return;

    if (reason === "scroll") this._featuredVideoPausedByScroll = false;
    if (reason === "external") this._featuredVideoPausedByExternal = false;

    if (this._featuredVideoPausedByScroll || this._featuredVideoPausedByExternal) return;

    this._featuredVideoPaused = false;
    this.postToFeaturedYoutubePlayer("playVideo");
  }

  // The embed uses mute=1, so start muted.
  public featuredVideoMuted: boolean = true;
  toggleMuteFeaturedVideo() {
    this.featuredVideoMuted = !this.featuredVideoMuted;
    // YouTube IFrame Player API commands are `mute()` and `unMute()`
    // (note the capital M).
    this.postToFeaturedYoutubePlayer(this.featuredVideoMuted ? "mute" : "unMute");
  }

  private postToFeaturedYoutubePlayer(func: "pauseVideo" | "playVideo" | "mute" | "unMute") {
    const iframe = this.featuredVideoIframe?.nativeElement;
    if (!iframe) return;

    // YouTube IFrame API listens for postMessage commands when enablejsapi=1.
    try {
      iframe.contentWindow?.postMessage(
        JSON.stringify({
          event: "command",
          func,
          args: [],
        }),
        "*"
      );
    } catch {
      // ignore
    }
  }

  private buildYoutubeEmbedUrl(videoId: string): SafeResourceUrl {
    const id = encodeURIComponent(videoId);
    const origin = typeof window !== "undefined" ? encodeURIComponent(window.location.origin) : "";
    const url =
      "https://www.youtube.com/embed/" +
      id +
      "?" +
      [
        // Required to control pause/play via postMessage.
        "enablejsapi=1",
        ...(origin ? ["origin=" + origin] : []),
        "autoplay=1",
        // Autoplay is most reliable when muted.
        "mute=1",
        "controls=0",
        // Don't load captions by default (users can still enable manually if desired).
        "cc_load_policy=0",
        "modestbranding=1",
        "rel=0",
        "playsinline=1",
        "iv_load_policy=3",
        "fs=0",
        "disablekb=1",
        "showinfo=0",
        // Loop the same video for a "hero background" feel.
        "loop=1",
        "playlist=" + id,
      ].join("&");

    return this._sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
